"use client";

import React, { useState } from "react";

import * as Icon from "@phosphor-icons/react/dist/ssr";
import { ProductType } from "../../type/ProductType";
import { useCart, notifyCartStorageChanged } from "../../context/CartContext";
import { useModalCartContext } from "../../context/ModalCartContext";
import { useWishlist } from "../../context/WishlistContext";
import { useModalWishlistContext } from "../../context/ModalWishlistContext";
import { useCompare } from "../../context/CompareContext";
import { useModalCompareContext } from "../../context/ModalCompareContext";
import { useModalQuickviewContext } from "../../context/ModalQuickviewContext";
import { useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import Rate from "../Other/Rate";
import { PriceDisplay } from "../Other/PriceDisplay";
import { isOutOfStock } from "../../utils/productStock";
import { useWhatsAppChat } from "../../context/WhatsAppChatContext";
import { PREORDER_WHATSAPP_INITIAL_MESSAGE } from "../../utils/preorderWhatsApp";

const HOME_SECTION_PYJAMAS_GANDORA = "PYJAMAS ET GANDORA";
const SALE_MARQUEE_DEFAULT = "Offre spéciale -15 %";
const SALE_MARQUEE_PYJAMAS_GANDORA =
  "Livraison gratuite pour tous les Gandora et Pyjamas avant l3id kbir";

function getSaleMarqueeLabel(sectionCategory?: string): string {
  if (
    sectionCategory?.trim().toUpperCase() === HOME_SECTION_PYJAMAS_GANDORA
  ) {
    return SALE_MARQUEE_PYJAMAS_GANDORA;
  }
  return SALE_MARQUEE_DEFAULT;
}

interface ProductProps {
  data: ProductType;
  type: string;
  showBuyButton?: boolean;
  /** Home category section title (e.g. PYJAMAS ET GANDORA) for section-specific sale banner */
  sectionCategory?: string;
}

const Product: React.FC<ProductProps> = React.memo(({
  data,
  type,
  showBuyButton = false,
  sectionCategory,
}) => {
  const [activeColor, setActiveColor] = useState<string>("");
  const [activeSize, setActiveSize] = useState<string>("");
  const [openQuickShop, setOpenQuickShop] = useState<boolean>(false);
  const { addToCart, updateCart, cartState } = useCart();
  const { openModalCart } = useModalCartContext();
  const { addToWishlist, removeFromWishlist, wishlistState } = useWishlist();
  const { openModalWishlist } = useModalWishlistContext();
  const { addToCompare, removeFromCompare, compareState } = useCompare();
  const { openModalCompare } = useModalCompareContext();
  const { openQuickview } = useModalQuickviewContext();
  const { openChat: openWhatsAppChat } = useWhatsAppChat();
  const router = useNavigate();

  const handleActiveColor = (item: string) => {
    setActiveColor(item);
  };

  const handleActiveSize = (item: string) => {
    setActiveSize(item);
  };

  const handleAddToCart = () => {
    if (!cartState.cartArray.find((item) => item.id === data.id)) {
      addToCart({ ...data });
      updateCart(data.id, data.quantityPurchase, activeSize, activeColor);
    } else {
      updateCart(data.id, data.quantityPurchase, activeSize, activeColor);
    }
    openModalCart();
  };

  const handleAddToCartProd = () => {
    const cartItemsJSON = localStorage.getItem("cartItems");
    const cartItems: { id: string | number; quantity: number }[] = cartItemsJSON
      ? JSON.parse(cartItemsJSON)
      : [];

    const newCartItem = {
      id: data?.id, // Ensure productMain.id is defined
      quantity: 1, // Use the quantity from your state or prop
    };

    if (!newCartItem.id) {
      console.error("Product ID is not defined.");
      return; 
    }

    const existingItemIndex = cartItems.findIndex(
      (item) => item.id === newCartItem.id
    );

    if (existingItemIndex >= 0) {
      cartItems[existingItemIndex].quantity += newCartItem.quantity;
    } else {
      cartItems.push(newCartItem);
    }
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
    notifyCartStorageChanged();
    openModalCart();
  };
  const handleAddToWishlist = () => {
    if (wishlistState.wishlistArray.some((item) => item.id === data.id)) {
      removeFromWishlist(data.id);
    } else {
      addToWishlist(data);
    }
    openModalWishlist();
  };

  const handleAddToCompare = () => {
    if (compareState.compareArray.length < 3) {
      if (compareState.compareArray.some((item) => item.id === data.id)) {
        removeFromCompare(data.id);
      } else {
        addToCompare(data);
      }
    } else {
      alert("Compare up to 3 products");
    }

    openModalCompare();
  };

  const handleQuickviewOpen = () => {
    openQuickview(data);
  };

  const handleDetailProduct = (productId: string) => {
    router(`/product/?id=${productId}`);
  };

  const handlePreorderViaWhatsApp = (e: React.MouseEvent) => {
    e.stopPropagation();
    const productName =
      (data.name && String(data.name).trim()) || `Produit ${data.id}`;
    openWhatsAppChat({
      product: { id: data.id, name: productName },
      initialMessage: PREORDER_WHATSAPP_INITIAL_MESSAGE,
    });
  };

  let percentSold = Math.floor((data.sold / data.quantity) * 100);
  const outOfStock = isOutOfStock(data);

  return (
    <>
    
      {type === "grid" ? (
        <div className="fx-reveal product-item grid-type group">
          <div
            onClick={() => handleDetailProduct(data.id)}
            className="product-main cursor-pointer block"
          >
            <div
              className={`product-thumb bg-white relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 group-hover:shadow-pink/20 ${outOfStock ? "opacity-[0.88]" : ""}`}
            >
              {/* Shimmer effect overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 z-10 pointer-events-none"></div>
              
              {/* Enhanced shadow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-pink/5 via-transparent to-blue/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {!outOfStock && data.new && (
                <div className="product-tag text-button-uppercase bg-green px-3 py-0.5 inline-block rounded-full absolute top-3 left-3 z-[1] shadow-md">
                  New
                </div>
              )}
              {outOfStock && (
                <div className="product-tag product-tag--rupture text-button-uppercase px-3 py-0.5 inline-block rounded-full absolute top-3 left-3 shadow-md">
                  Rupture — précommande
                </div>
              )}
              {!outOfStock && data.sale && (
                <div className="product-tag text-button-uppercase text-white bg-red px-3 py-0.5 inline-block rounded-full absolute top-3 left-3 z-[1] shadow-md">
                  Sale
                </div>
              )}
              <div className="list-action-right absolute top-3 right-3 max-lg:hidden">
                <div
                  className={`add-wishlist-btn w-[32px] h-[32px] flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm duration-300 relative shadow-md hover:shadow-lg ${
                    wishlistState.wishlistArray.some(
                      (item) => item.id === data.id
                    )
                      ? "active"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToWishlist();
                  }}
                >
                  <div className="tag-action bg-black text-white caption2 px-1.5 py-0.5 rounded-sm">
                    Add To Wishlist
                  </div>
                  {wishlistState.wishlistArray.some(
                    (item) => item.id === data.id
                  ) ? (
                    <>
                      <Icon.Heart
                        size={18}
                        weight="fill"
                        className="text-white"
                      />
                    </>
                  ) : (
                    <>
                      <Icon.Heart size={18} />
                    </>
                  )}
                </div>
                <div
                  className={`compare-btn w-[32px] h-[32px] flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm duration-300 relative mt-2 shadow-md hover:shadow-lg ${
                    compareState.compareArray.some(
                      (item) => item.id === data.id
                    )
                      ? "active"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToCompare();
                  }}
                >
                  <div className="tag-action bg-black text-white caption2 px-1.5 py-0.5 rounded-sm">
                    Compare Product
                  </div>
                  <Icon.Repeat size={18} className="compare-icon" />
                  <Icon.CheckCircle size={20} className="checked-icon" />
                </div>
              </div>
              <div className="product-img w-full h-full aspect-[3/4] overflow-hidden" style={{ minHeight: '280px', maxHeight: '320px' }}>
                {activeColor ? (
                  <>
                    {
                      <img
                        src={
                          data.variation.find(
                            (item) => item.color === activeColor
                          )?.image ?? ""
                        }
                        width={500}
                        height={500}
                        alt={data.name}
                        className="w-full h-full object-contain object-center duration-700 group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                    }
                  </>
                ) : (
                  <>
                      <img
                        src={`/images/${data.id}.jpg`}
                        width={500}
                        height={500}
                        alt={data.name}
                        onError={(e) => {
                          e.currentTarget.src = "/default.png"; 
                        }}
                        className="w-full h-full object-contain object-center duration-700 group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                  </>
                )}
              </div>
              {data.sale && (
                <>
                  <Marquee
                    className={`banner-sale-auto bg-pink absolute bottom-0 left-0 w-full py-1.5 ${
                      sectionCategory?.trim().toUpperCase() ===
                      HOME_SECTION_PYJAMAS_GANDORA
                        ? "banner-sale-auto--pyjamas"
                        : ""
                    }`}
                  >
                    {[0, 1, 2, 3, 4].map((index) => (
                      <React.Fragment key={index}>
                        <span className="banner-sale-marquee-text caption2 font-semibold uppercase text-white px-2.5">
                          {getSaleMarqueeLabel(sectionCategory)}
                        </span>
                        <Icon.Lightning
                          weight="fill"
                          size={14}
                          className="banner-sale-marquee-icon text-black shrink-0"
                        />
                      </React.Fragment>
                    ))}
                  </Marquee>
                </>
              )}
              <div className="list-action flex flex-col items-center justify-center gap-3 absolute inset-0 max-lg:hidden">
                <div
                  className="quick-view-btn w-auto min-w-[200px] text-button-uppercase py-2 px-6 text-center rounded-full duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
                  style={{ background: '#b1d3cb', color: 'black' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuickviewOpen();
                  }}
                >
                  <Icon.Eye size={18} className="inline-block mr-2 align-middle" />
                  Vue rapide
                </div>
                {outOfStock ? (
                  <button
                    type="button"
                    onClick={handlePreorderViaWhatsApp}
                    className="add-cart-btn w-auto min-w-[200px] text-button-uppercase py-2 px-6 text-center rounded-full duration-500 bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-md hover:shadow-lg transform hover:scale-105 flex items-center justify-center gap-2"
                  >
                    <Icon.WhatsappLogo size={18} weight="fill" />
                    Précommander
                  </button>
                ) : data.action === "add to cart" ? (
                  <div
                    className="add-cart-btn w-auto min-w-[200px] text-button-uppercase py-2 px-6 text-center rounded-full duration-500 bg-white hover:bg-black hover:text-white shadow-md hover:shadow-lg transform hover:scale-105"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCartProd();
                    }}
                  >
                    Ajouter au panier
                  </div>
                ) : (
                  <>
                    
                    <div
                      className={`quick-shop-block absolute left-5 right-5 bg-white p-5 rounded-[20px] shadow-lg ${
                        openQuickShop ? "open" : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <div className="list-size flex items-center justify-center flex-wrap gap-2">
                        {data.sizes.map((item, index) => (
                          <div
                            className={`size-item w-10 h-10 rounded-full flex items-center justify-center text-button bg-white border border-line shadow-sm hover:shadow-md ${
                              activeSize === item ? "active" : ""
                            }`}
                            key={index}
                            onClick={() => handleActiveSize(item)}
                          >
                            {item}
                          </div>
                        ))}
                      </div>
                      <div
                        className="bg-black text-white w-full text-center rounded-3xl hover:bg-greenprimary py-2 mt-4 shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                        onClick={() => {
                          handleAddToCartProd();
                          setOpenQuickShop(false);
                        }}
                      >
                        Ajouter au panier
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="product-infor mt-4 lg:mb-7">
              <div className="product-name text-title duration-300 truncate">
                {data.name}
              </div>
              
              {data.variation.length > 0 && data.action === "add to cart" && (
                <div className="list-color py-2 max-md:hidden flex items-center gap-3 flex-wrap duration-500">
                  {data.variation.map((item, index) => (
                    <div
                      key={index}
                      className={`color-item w-8 h-8 rounded-full duration-300 relative ${
                        activeColor === item.color ? "active" : ""
                      }`}
                      style={{ backgroundColor: `${item.colorCode}` }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleActiveColor(item.color);
                      }}
                    >
                      <div className="tag-action bg-black text-white caption2 capitalize px-1.5 py-0.5 rounded-sm">
                        {item.color}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {data.variation.length > 0 && data.action === "quick shop" && (
                <div className="list-color-image max-md:hidden flex items-center gap-3 flex-wrap duration-500">
                  {data.variation.map((item, index) => (
                    <div
                      className={`color-item w-12 h-12 rounded-xl duration-300 relative ${
                        activeColor === item.color ? "active" : ""
                      }`}
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleActiveColor(item.color);
                      }}
                    >
                      <img
                        src={item.colorImage}
                        width={100}
                        height={100}
                        alt="color"
                        className="rounded-xl w-full h-full object-cover"
                      />
                      <div className="tag-action bg-black text-white caption2 capitalize px-1.5 py-0.5 rounded-sm">
                        {item.color}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div
                className={`product-price-block flex items-center gap-2 flex-wrap mt-1 duration-300 relative z-[1] ${
                  showBuyButton
                    ? "group-hover:opacity-0 group-hover:-translate-y-1"
                    : ""
                }`}
              >
                <div className="product-price text-title group-hover:font-bold transition-all duration-300">
                  <PriceDisplay
                    amount={data.price}
                    amountClassName="group-hover:text-pink transition-colors duration-300"
                  />
                </div>
                {data.rate > 0 && (
                  <>
                    <div className="product-origin-price caption1 text-secondary2">
                      <del>
                        <PriceDisplay amount={data.originPrice} size="sm" />
                      </del>
                    </div>
                    <div className="product-sale caption1 font-medium bg-green px-3 py-0.5 inline-block rounded-full">
                      -{data.rate}%
                    </div>
                  </>
                )}
              </div>
              {showBuyButton && (
                <span className="mt-2 inline-flex w-full items-center justify-center rounded-lg border border-pink/35 bg-pink/5 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-pink transition-colors duration-200 group-hover:bg-pink group-hover:text-white">
                  <span className="inline-flex items-center gap-1.5 group-hover:hidden">
                    <Icon.ShoppingBagOpen size={14} weight="duotone" />
                    <span>Acheter</span>
                  </span>
                  <span className="hidden group-hover:inline-flex items-center gap-1.5">
                    <Icon.ShoppingBagOpen size={14} weight="fill" />
                    <PriceDisplay
                      amount={data.price}
                      size="sm"
                      amountClassName="!text-white"
                      currencyClassName="!text-white/90"
                    />
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          {type === "list" ? (
            <>
              <div className="fx-reveal product-item list-type">
                <div className="product-main cursor-pointer flex lg:items-center sm:justify-between gap-7 max-lg:gap-5">
                  <div
                    onClick={() => handleDetailProduct(data.id)}
                    className="product-thumb bg-white relative overflow-hidden rounded-2xl block max-sm:w-1/2"
                  >
                    {!outOfStock && data.new && (
                      <div className="product-tag text-button-uppercase bg-green px-3 py-0.5 inline-block rounded-full absolute top-3 left-3 z-[1]">
                        New
                      </div>
                    )}
                    {outOfStock && (
                      <div className="product-tag text-button-uppercase text-white px-3 py-0.5 inline-block rounded-full absolute top-3 left-3 z-[1] bg-gradient-to-r from-amber-600 to-orange-600 ring-1 ring-amber-200/60 shadow-md">
                        Rupture — précommande
                      </div>
                    )}
                    {!outOfStock && data.sale && (
                      <div className="product-tag text-button-uppercase text-white bg-red px-3 py-0.5 inline-block rounded-full absolute top-3 left-3 z-[1]">
                        Sale
                      </div>
                    )}
                    <div className="product-img w-full aspect-[3/4] rounded-2xl overflow-hidden" style={{ minHeight: '280px', maxHeight: '320px' }}>
                      {data.thumbImage.map((img, index) => (
                        <img
                          key={index}
                          src={img}
                          width={500}
                          height={500}
                          alt={data.name}
                          className="w-full h-full object-contain object-center duration-700"
                        />
                      ))}
                    </div>
                    <div className="list-action px-5 absolute w-full bottom-5 max-lg:hidden">
                      <div
                        className={`quick-shop-block absolute left-5 right-5 bg-white p-5 rounded-[20px] ${
                          openQuickShop ? "open" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <div className="list-size flex items-center justify-center flex-wrap gap-2">
                          {data.sizes.map((item, index) => (
                            <div
                              className={`size-item ${
                                item !== "freesize" ? "w-10 h-10" : "h-10 px-4"
                              } flex items-center justify-center text-button bg-white rounded-full border border-line ${
                                activeSize === item ? "active" : ""
                              }`}
                              key={index}
                              onClick={() => handleActiveSize(item)}
                            >
                              {item}
                            </div>
                          ))}
                        </div>
                        <div
                          className="button-main w-full text-center rounded-full py-3 mt-4"
                          onClick={() => {
                            handleAddToCartProd();
                            setOpenQuickShop(false);
                          }}
                        >
                          Add To cart
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex sm:items-center gap-7 max-lg:gap-4 max-lg:flex-wrap max-lg:w-full max-sm:flex-col max-sm:w-1/2">
                    <div className="product-infor max-sm:w-full">
                      <div
                        onClick={() => handleDetailProduct(data.id)}
                        className="product-name heading6 inline-block duration-300"
                      >
                         {data.name}
                      </div>
                      <div className="product-price-block flex items-center gap-2 flex-wrap mt-2 duration-300 relative z-[1]">
                        <div className="product-price text-title group-hover:font-bold transition-all duration-300">
                          <PriceDisplay
                            amount={data.price}
                            amountClassName="group-hover:text-pink transition-colors duration-300"
                          />
                        </div>
                        <div className="product-origin-price caption1 text-secondary2">
                          <del>
                            <PriceDisplay amount={data.originPrice} size="sm" />
                          </del>
                        </div>
                        {data.originPrice && (
                          <div className="product-sale caption1 font-medium bg-green px-3 py-0.5 inline-block rounded-full">
                            -{data.rate}%
                          </div>
                        )}
                      </div>
                      {data.variation.length > 0 &&
                      data.action === "add to cart" ? (
                        <div className="list-color max-md:hidden py-2 mt-5 mb-1 flex items-center gap-3 flex-wrap duration-300">
                          {data.variation.map((item, index) => (
                            <div
                              key={index}
                              className={`color-item w-8 h-8 rounded-full duration-300 relative`}
                              style={{ backgroundColor: `${item.colorCode}` }}
                            >
                              <div className="tag-action bg-black text-white caption2 capitalize px-1.5 py-0.5 rounded-sm">
                                {item.color}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          {data.variation.length > 0 &&
                          data.action === "quick shop" ? (
                            <>
                              <div className="list-color flex items-center gap-2 flex-wrap mt-5">
                                {data.variation.map((item, index) => (
                                  <div
                                    className={`color-item w-12 h-12 rounded-xl duration-300 relative ${
                                      activeColor === item.color ? "active" : ""
                                    }`}
                                    key={index}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleActiveColor(item.color);
                                    }}
                                  >
                                    <img
                                      src={item.colorImage}
                                      width={100}
                                      height={100}
                                      alt="color"
                                      className="rounded-xl"
                                    />
                                    <div className="tag-action bg-black text-white caption2 capitalize px-1.5 py-0.5 rounded-sm">
                                      {item.color}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : (
                            <></>
                          )}
                        </>
                      )}
                      <div className="text-secondary desc mt-5 max-sm:hidden">
                        {data.description}
                      </div>
                    </div>
                    <div className="action w-fit flex flex-col items-center justify-center">
                      {outOfStock ? (
                        <button
                          type="button"
                          onClick={handlePreorderViaWhatsApp}
                          className="button-main whitespace-nowrap py-2 px-9 max-lg:px-5 rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white flex items-center justify-center gap-2 shadow-md"
                        >
                          <Icon.WhatsappLogo size={18} weight="fill" />
                          Précommander
                        </button>
                      ) : (
                        <div
                          className="quick-shop-btn button-main whitespace-nowrap py-2 px-9 max-lg:px-5 rounded-full bg-white text-black border border-black hover:bg-black hover:text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenQuickShop(!openQuickShop);
                          }}
                        >
                          Quick Shop
                        </div>
                      )}
                      <div className="list-action-right flex items-center justify-center gap-3 mt-4">
                        <div
                          className={`add-wishlist-btn w-[32px] h-[32px] flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm duration-300 relative shadow-md hover:shadow-lg ${
                            wishlistState.wishlistArray.some(
                              (item) => item.id === data.id
                            )
                              ? "active"
                              : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToWishlist();
                          }}
                        >
                          <div className="tag-action bg-black text-white caption2 px-1.5 py-0.5 rounded-sm">
                            Add To Wishlist
                          </div>
                          {wishlistState.wishlistArray.some(
                            (item) => item.id === data.id
                          ) ? (
                            <>
                              <Icon.Heart
                                size={18}
                                weight="fill"
                                className="text-white"
                              />
                            </>
                          ) : (
                            <>
                              <Icon.Heart size={18} />
                            </>
                          )}
                        </div>
                        <div
                          className={`compare-btn w-[32px] h-[32px] flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm duration-300 relative mt-2 shadow-md hover:shadow-lg ${
                            compareState.compareArray.some(
                              (item) => item.id === data.id
                            )
                              ? "active"
                              : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToCompare();
                          }}
                        >
                          <div className="tag-action bg-black text-white caption2 px-1.5 py-0.5 rounded-sm">
                            Compare Product
                          </div>
                          <Icon.ArrowsCounterClockwise
                            size={18}
                            className="compare-icon"
                          />
                          <Icon.CheckCircle
                            size={20}
                            className="checked-icon"
                          />
                        </div>
                        <div
                          className="quick-view-btn-list w-[32px] h-[32px] flex items-center justify-center rounded-full bg-white duration-300 relative"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickviewOpen();
                          }}
                        >
                          <div className="tag-action bg-black text-white caption2 px-1.5 py-0.5 rounded-sm">
                            Quick View
                          </div>
                          <Icon.Eye size={18} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <></>
          )}
        </>
      )}

      {type === "marketplace" ? (
        <div
          className="fx-reveal product-item style-marketplace p-4 border border-line rounded-2xl"
          onClick={() => handleDetailProduct(data.id)}
        >
          <div className="bg-img relative w-full">
            {outOfStock && (
              <div className="product-tag product-tag--rupture absolute top-2 left-2 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full shadow-sm">
                Rupture — précommande
              </div>
            )}
            <img
              className="w-full aspect-square"
              width={5000}
              height={5000}
              src={data.thumbImage[0]}
              alt="img"
            />
            <div className="list-action flex flex-col gap-1 absolute top-0 right-0">
              <span
                className={`add-wishlist-btn w-8 h-8 bg-white flex items-center justify-center rounded-full box-shadow-small duration-300 ${
                  wishlistState.wishlistArray.some(
                    (item) => item.id === data.id
                  )
                    ? "active"
                    : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToWishlist();
                }}
              >
                {wishlistState.wishlistArray.some(
                  (item) => item.id === data.id
                ) ? (
                  <>
                    <Icon.Heart
                      size={18}
                      weight="fill"
                      className="text-white"
                    />
                  </>
                ) : (
                  <>
                    <Icon.Heart size={18} />
                  </>
                )}
              </span>
              <span
                className={`compare-btn w-8 h-8 bg-white flex items-center justify-center rounded-full box-shadow-small duration-300 ${
                  compareState.compareArray.some((item) => item.id === data.id)
                    ? "active"
                    : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToCompare();
                }}
              >
                <Icon.Repeat size={18} className="compare-icon" />
                <Icon.CheckCircle size={20} className="checked-icon" />
              </span>
              <span
                className="quick-view-btn w-8 h-8 bg-white flex items-center justify-center rounded-full box-shadow-small duration-300"
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuickviewOpen();
                }}
              >
                <Icon.Eye />
              </span>
              <span
                className={`add-cart-btn w-8 h-8 bg-white flex items-center justify-center rounded-full box-shadow-small duration-300 ${
                  outOfStock ? "text-[#25D366]" : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (outOfStock) {
                    handlePreorderViaWhatsApp(e);
                  } else {
                    handleAddToCartProd();
                  }
                }}
              >
                {outOfStock ? (
                  <Icon.WhatsappLogo size={18} weight="fill" />
                ) : (
                  <Icon.ShoppingBagOpen />
                )}
              </span>
            </div>
          </div>
          <div className="product-infor mt-4">
            <span className="text-title">{data.name}</span>
            <div className="flex gap-0.5 mt-1">
              <Rate currentRate={data.rate} size={16} />
            </div>
            <span className="text-title inline-block mt-1">
              <PriceDisplay amount={data.price} />
            </span>
          </div>
        </div>
      ) : (
        <></>
      )}
    </>
  );
});

Product.displayName = 'Product';

export default Product;



/*
 <img
  src={`images/${data.id}.jpg`}
  width={500}
  height={500}
  alt={data.name}
  className="w-full h-full object-cover duration-700"
                      />
                      
{data.thumbImage.map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        width={500}
                        height={500}
                        alt={data.name}
                        className="w-full h-full object-cover duration-700"
                      />
                    ))}
                      */